import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiPause, FiSettings, FiWifi, FiClock, FiMonitor, FiRefreshCw, FiX, FiUpload, FiAlertCircle, FiCheck, FiDownload } = FiIcons;

const MultiScreenSync = () => {
  const { groupId, screenIndex } = useParams();
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [syncStatus, setSyncStatus] = useState('waiting');
  const [readyScreens, setReadyScreens] = useState([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showMediaLibraryModal, setShowMediaLibraryModal] = useState(false);
  const [activeMedia, setActiveMedia] = useState(null);
  const [availableMedia, setAvailableMedia] = useState([]);
  const [language, setLanguage] = useState('en');

  const videoRef = useRef(null);
  const wsRef = useRef(null);

  const translations = {
    en: {
      connecting: 'Connecting to Sync Network...',
      waiting: 'Waiting for All Screens...',
      ready: 'Ready to Play',
      setup: 'Multi-Screen Setup',
      screen: 'Screen',
      of: 'of',
      groupId: 'Group ID:',
      startPlayback: 'Start Synchronized Playback',
      synchronized: 'Synchronized',
      disconnected: 'Disconnected',
      syncStatus: 'Sync Status',
      settings: 'Settings',
      close: 'Close',
      mediaLibrary: 'Media Library',
      noMedia: 'No media available for this screen',
      selectMedia: 'Select media to display',
      uploadMedia: 'Upload Media',
      applyMedia: 'Apply Selected Media'
    },
    de: {
      connecting: 'Verbindung zum Sync-Netzwerk wird hergestellt...',
      waiting: 'Warten auf alle Bildschirme...',
      ready: 'Bereit zum Abspielen',
      setup: 'Multi-Screen-Einrichtung',
      screen: 'Bildschirm',
      of: 'von',
      groupId: 'Gruppen-ID:',
      startPlayback: 'Synchronisierte Wiedergabe starten',
      synchronized: 'Synchronisiert',
      disconnected: 'Getrennt',
      syncStatus: 'Sync-Status',
      settings: 'Einstellungen',
      close: 'Schließen',
      mediaLibrary: 'Medienbibliothek',
      noMedia: 'Keine Medien für diesen Bildschirm verfügbar',
      selectMedia: 'Medien zum Anzeigen auswählen',
      uploadMedia: 'Medien hochladen',
      applyMedia: 'Ausgewählte Medien anwenden'
    }
  };

  const t = translations[language];

  // Mock configuration - in a real app, this would come from the server
  const groupConfig = {
    id: groupId || 'demo-group',
    totalScreens: 3,
    screenIndex: parseInt(screenIndex || 1),
    videoSources: [
      '/videos/split-video-part1.mp4', // Left screen
      '/videos/split-video-part2.mp4', // Middle screen
      '/videos/split-video-part3.mp4', // Right screen
    ],
    masterScreen: 1, // Middle screen is the master that controls sync
  };

  // Initialize WebSocket connection
  useEffect(() => {
    // In a real app, this would connect to a real WebSocket server
    const connectWebSocket = () => {
      // Simulate WebSocket connection
      console.log('Connecting to sync server...');
      
      // Simulate connection process
      setTimeout(() => {
        setConnectionStatus('connected');
        console.log('Connected to sync server');
        
        // Simulate other screens connecting
        setTimeout(() => {
          setReadyScreens([1]);
          setTimeout(() => {
            setReadyScreens([1, 2]);
            setTimeout(() => {
              setReadyScreens([1, 2, 3]);
              setSyncStatus('ready');
            }, 1000);
          }, 1500);
        }, 1000);
      }, 2000);
      
      // In a real app, this would be actual WebSocket message handlers
      wsRef.current = {
        send: (message) => {
          console.log('Sending message:', message);
        },
        close: () => {
          console.log('Closing WebSocket connection');
          setConnectionStatus('disconnected');
        }
      };
    };
    
    connectWebSocket();
    
    // Mock available media
    setAvailableMedia([
      { id: 1, name: 'Product Demo', type: 'video', thumbnail: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=300&h=200&fit=crop' },
      { id: 2, name: 'Company Logo', type: 'image', thumbnail: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=300&h=200&fit=crop' },
      { id: 3, name: 'Special Offer', type: 'video', thumbnail: 'https://images.unsplash.com/photo-1551818255-e6e10975bc17?w=300&h=200&fit=crop' }
    ]);
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Handle playback synchronization
  useEffect(() => {
    if (!videoRef.current) return;
    
    const handleTimeUpdate = () => {
      const currentTime = videoRef.current.currentTime;
      setCurrentTime(currentTime);
      
      // In a real app, this would send the current time to other screens
      if (groupConfig.screenIndex === groupConfig.masterScreen && wsRef.current) {
        wsRef.current.send(JSON.stringify({ 
          type: 'sync', 
          time: currentTime 
        }));
      }
    };
    
    const handlePlay = () => {
      setIsPlaying(true);
      if (groupConfig.screenIndex === groupConfig.masterScreen && wsRef.current) {
        wsRef.current.send(JSON.stringify({ type: 'play' }));
      }
    };
    
    const handlePause = () => {
      setIsPlaying(false);
      if (groupConfig.screenIndex === groupConfig.masterScreen && wsRef.current) {
        wsRef.current.send(JSON.stringify({ type: 'pause' }));
      }
    };
    
    videoRef.current.addEventListener('timeupdate', handleTimeUpdate);
    videoRef.current.addEventListener('play', handlePlay);
    videoRef.current.addEventListener('pause', handlePause);
    
    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener('timeupdate', handleTimeUpdate);
        videoRef.current.removeEventListener('play', handlePlay);
        videoRef.current.removeEventListener('pause', handlePause);
      }
    };
  }, [groupConfig.masterScreen, groupConfig.screenIndex]);

  const startSyncedPlayback = () => {
    if (syncStatus !== 'ready') return;
    
    // In a real app, this would ensure all videos start at exactly the same time
    if (groupConfig.screenIndex === groupConfig.masterScreen) {
      // Master screen initiates the playback
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play();
          wsRef.current.send(JSON.stringify({ 
            type: 'start', 
            timestamp: Date.now() + 1000 // Start in 1 second
          }));
        }
      }, 100);
    } else {
      // Other screens wait for the start signal
      // In a real implementation, they would receive the start signal via WebSocket
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play();
        }
      }, 1000);
    }
    
    setSyncStatus('playing');
  };

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
  };

  const handleSelectMedia = (media) => {
    setActiveMedia(media);
  };

  const handleApplyMedia = () => {
    // In a real app, this would update the video source
    setShowMediaLibraryModal(false);
    toast.success('Media applied successfully');
  };

  // Display the appropriate video section based on screen index
  const videoSource = groupConfig.videoSources[groupConfig.screenIndex - 1];
  
  // For demonstration, show image if active media is an image
  const isImage = activeMedia?.type === 'image';

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Status Overlay (shown during connection and sync) */}
      {(connectionStatus !== 'connected' || syncStatus === 'waiting') && (
        <div className="absolute inset-0 bg-black z-50 flex flex-col items-center justify-center">
          <div className="w-16 h-16 mb-6">
            {connectionStatus === 'connecting' ? (
              <div className="animate-spin w-full h-full border-4 border-purple-400 border-t-transparent rounded-full"></div>
            ) : (
              <div className={`w-full h-full rounded-full flex items-center justify-center ${syncStatus === 'ready' ? 'bg-green-500' : 'bg-yellow-500'}`}>
                <SafeIcon icon={syncStatus === 'ready' ? FiPlay : FiRefreshCw} className="text-white text-2xl" />
              </div>
            )}
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-4">
            {connectionStatus === 'connecting' ? t.connecting : 
             syncStatus === 'waiting' ? t.waiting :
             t.ready}
          </h2>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6">
            <p className="text-white mb-2">{t.setup}</p>
            <p className="text-white/70 text-sm">{t.screen} {groupConfig.screenIndex} {t.of} {groupConfig.totalScreens}</p>
            <p className="text-white/70 text-sm mt-1">{t.groupId} {groupConfig.id}</p>
          </div>
          
          {/* Screen status indicators */}
          <div className="flex space-x-4 mb-8">
            {Array.from({ length: groupConfig.totalScreens }, (_, i) => (
              <div key={i} className="text-center">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-2 ${readyScreens.includes(i+1) ? 'bg-green-500' : 'bg-white/20'}`}>
                  <SafeIcon icon={FiMonitor} className="text-white text-xl" />
                </div>
                <p className={`text-sm ${readyScreens.includes(i+1) ? 'text-green-400' : 'text-white/50'}`}>
                  {t.screen} {i+1}
                </p>
              </div>
            ))}
          </div>
          
          {syncStatus === 'ready' && (
            <button 
              onClick={startSyncedPlayback}
              className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
            >
              {t.startPlayback}
            </button>
          )}
        </div>
      )}

      {/* Main Content Display */}
      {isImage ? (
        <img 
          src={activeMedia?.thumbnail} 
          alt={activeMedia?.name} 
          className="w-full h-full object-cover"
        />
      ) : (
        <video 
          ref={videoRef}
          src={videoSource} 
          className="w-full h-full object-cover"
          playsInline
          preload="auto"
          muted={groupConfig.screenIndex !== groupConfig.masterScreen} // Only master screen plays audio
          onEnded={() => setIsPlaying(false)}
        />
      )}

      {/* Screen Border Indicator */}
      <div className={`absolute inset-0 pointer-events-none border-4 ${showControls ? 'border-purple-500 opacity-50' : 'border-transparent'}`}></div>
      
      {/* Top Status Bar */}
      <div className="absolute top-0 left-0 right-0 p-6 z-10">
        <div className="flex justify-between items-center text-white">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
              <SafeIcon icon={FiWifi} className={connectionStatus === 'connected' ? 'text-green-400' : 'text-red-400'} />
              <span className="text-sm">{connectionStatus === 'connected' ? t.synchronized : t.disconnected}</span>
            </div>
            <div className="flex items-center space-x-2 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
              <SafeIcon icon={FiMonitor} />
              <span className="text-sm">{t.screen} {groupConfig.screenIndex}/{groupConfig.totalScreens}</span>
            </div>
          </div>
          <div className="flex items-center space-x-2 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
            <SafeIcon icon={FiClock} />
            <span className="text-sm">{Math.floor(currentTime / 60)}:{Math.floor(currentTime % 60).toString().padStart(2, '0')}</span>
          </div>
        </div>
      </div>
      
      {/* Controls Overlay */}
      <AnimatePresence>
        {showControls && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-20"
          >
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 max-w-md">
              <h2 className="text-2xl font-bold text-white mb-6 text-center">Multi-Screen Controls</h2>
              
              <div className="flex items-center justify-center space-x-6 mb-8">
                <button 
                  onClick={handlePlayPause}
                  className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
                >
                  <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="text-white text-2xl" />
                </button>
              </div>
              
              <div className="mb-6">
                <p className="text-white/70 mb-2">{t.syncStatus}</p>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${connectionStatus === 'connected' ? 'bg-green-400' : 'bg-red-400'}`}></div>
                  <span className="text-white">{connectionStatus === 'connected' ? t.synchronized : t.disconnected}</span>
                </div>
              </div>

              <div className="mb-6">
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                >
                  <option value="en">English</option>
                  <option value="de">Deutsch</option>
                </select>
              </div>
              
              <div className="flex space-x-4">
                <button 
                  onClick={() => {
                    setShowControls(false);
                    setShowSettingsModal(true);
                  }}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-white/20 border border-white/30 text-white rounded-xl hover:bg-white/30 transition-colors"
                >
                  <SafeIcon icon={FiSettings} />
                  <span>{t.settings}</span>
                </button>
                <button 
                  onClick={() => {
                    setShowControls(false);
                    setShowMediaLibraryModal(true);
                  }}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-white/20 border border-white/30 text-white rounded-xl hover:bg-white/30 transition-colors"
                >
                  <SafeIcon icon={FiUpload} />
                  <span>{t.mediaLibrary}</span>
                </button>
                <button 
                  onClick={() => setShowControls(false)}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
                >
                  {t.close}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Settings Modal */}
      <AnimatePresence>
        {showSettingsModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-30"
          >
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 max-w-lg w-full">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{t.settings}</h2>
                <button 
                  onClick={() => setShowSettingsModal(false)}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="text-white/70" />
                </button>
              </div>
              
              <div className="space-y-6 mb-6">
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Language
                  </label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                  >
                    <option value="en">English</option>
                    <option value="de">Deutsch</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Sync Tolerance (ms)
                  </label>
                  <input 
                    type="number" 
                    defaultValue={50}
                    min={10}
                    max={500}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                  <p className="text-white/50 text-xs mt-1">Lower values require better network conditions</p>
                </div>
                
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Screen Information
                  </label>
                  <div className="bg-white/5 p-4 rounded-xl">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/70">Group ID:</span>
                      <span className="text-white">{groupConfig.id}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/70">Screen Position:</span>
                      <span className="text-white">{groupConfig.screenIndex} of {groupConfig.totalScreens}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/70">Master Screen:</span>
                      <span className="text-white">{groupConfig.masterScreen}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-4">
                <Link 
                  to="/tv/sync-setup"
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-white/20 border border-white/30 text-white rounded-xl hover:bg-white/30 transition-colors"
                >
                  <SafeIcon icon={FiSettings} />
                  <span>Reconfigure</span>
                </Link>
                <button 
                  onClick={() => setShowSettingsModal(false)}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
                >
                  <SafeIcon icon={FiCheck} />
                  <span>Save Settings</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Media Library Modal */}
      <AnimatePresence>
        {showMediaLibraryModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-30"
          >
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 max-w-2xl w-full">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{t.mediaLibrary}</h2>
                <button 
                  onClick={() => setShowMediaLibraryModal(false)}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="text-white/70" />
                </button>
              </div>
              
              <p className="text-white/70 mb-4">{t.selectMedia}</p>
              
              {availableMedia.length === 0 ? (
                <div className="bg-white/5 rounded-xl p-6 text-center mb-6">
                  <SafeIcon icon={FiAlertCircle} className="text-4xl text-white/30 mx-auto mb-3" />
                  <p className="text-white/70">{t.noMedia}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                  {availableMedia.map((media) => (
                    <div 
                      key={media.id}
                      onClick={() => handleSelectMedia(media)}
                      className={`bg-white/5 border rounded-xl overflow-hidden cursor-pointer transition-all ${
                        activeMedia?.id === media.id 
                          ? 'border-purple-400 ring-2 ring-purple-400/20' 
                          : 'border-white/20 hover:border-white/40'
                      }`}
                    >
                      <div className="aspect-video relative">
                        <img src={media.thumbnail} alt={media.name} className="w-full h-full object-cover" />
                        <div className="absolute top-2 right-2 bg-black/50 px-2 py-1 rounded text-xs text-white">
                          {media.type}
                        </div>
                        {activeMedia?.id === media.id && (
                          <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                              <SafeIcon icon={FiCheck} className="text-white" />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <p className="text-white truncate">{media.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex space-x-4">
                <button 
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-white/20 border border-white/30 text-white rounded-xl hover:bg-white/30 transition-colors"
                >
                  <SafeIcon icon={FiUpload} />
                  <span>{t.uploadMedia}</span>
                </button>
                <button 
                  onClick={handleApplyMedia}
                  disabled={!activeMedia}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                >
                  <SafeIcon icon={FiCheck} />
                  <span>{t.applyMedia}</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Click Area for Controls */}
      <button 
        onClick={() => setShowControls(!showControls)} 
        className="absolute inset-0 w-full h-full bg-transparent focus:outline-none z-10"
        aria-label="Show controls"
      />
    </div>
  );
};

export default MultiScreenSync;